import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service, UserListOption } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import FormSelectWithActionComponent from 'webapp/components/form/select-with-action';
import type { ActionOption } from 'webapp/components/form/select-with-action';
import RowErrorComponent from 'webapp/components/row-error';

import type Space from 'webapp/models-custom/space';

interface Args {
	model: Space;
	isDeleteBtnShown: boolean;
	usersForAccountManager: UserListOption[];
	submitInProgress: boolean;
	onInvite: (forClient?: Space) => void;
	onDelete: (item: Space) => void;
}

export default class ClientNewRowComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;

	prefilledUrl = 'https://';

	get dropdownActions(): ActionOption[] {
		if (this.currentUserService.isAdmin) {
			return [
				{
					id: null,
					text: 'Invite new account manager...',
					isSpecial: true,
					position: 'bottom',
					onClick: () => {
						// Newly invited user is set as an owner for this client (user model) in modal/client.js,
						// so it must be forwarded to it
						this.args.onInvite(this.args.model);
					},
					html: '<a role="button" class="invite"><ui-icon name="Plus" size="sm"></ui-icon> Invite new account manager...</a>',
				},
			];
		}

		return [];
	}

	get ownerIdString() {
		return this.args.model.ownerId?.toString() ?? '';
	}

	@action
	chooseOwner(ownerId: string | null) {
		this.args.model.ownerId = +ownerId!;
	}

	@action
	focusWebsiteUrl() {
		if (!this.args.model.websiteUrl) {
			this.args.model.websiteUrl = this.prefilledUrl;
		}
	}

	@action
	unfocusWebsiteUrl() {
		if (this.args.model.websiteUrl === this.prefilledUrl) {
			this.args.model.websiteUrl = '';
		}
	}
	@action
	deleteRow() {
		this.args.onDelete(this.args.model);
	}
	<template>
		<div class="row item {{if @isDeleteBtnShown 'item--show-delete'}}">
			<div class="col">

				<FormInputComponent
					name="companyName"
					@value={{@model.companyName}}
					@autofocus={{true}}
					maxlength="50"
					placeholder="Account name"
					@hideFloatLabel={{true}}
					disabled={{@submitInProgress}}
				/>

				<RowErrorComponent @items={{@model.errors.companyName}} />

			</div>
			<div class="col col--connector">

				<FormInputComponent
					name="websiteUrl"
					maxlength="255"
					placeholder="https://"
					disabled={{@submitInProgress}}
					@value={{@model.websiteUrl}}
					@hideFloatLabel={{true}}
					@focusIn={{this.focusWebsiteUrl}}
					@focusOut={{this.unfocusWebsiteUrl}}
				/>

				<RowErrorComponent @items={{@model.errors.websiteUrl}} />

			</div>
			<div class="col col--connector">

				<FormSelectWithActionComponent
					name="ownerId"
					@selected={{this.ownerIdString}}
					@options={{@usersForAccountManager}}
					@actionOptions={{this.dropdownActions}}
					@onChange={{this.chooseOwner}}
					@placeholder="Please select"
					@isRequired={{true}}
					@isNumeric={{false}}
					@showFloatlabel={{false}}
				/>

				<RowErrorComponent @items={{@model.errors.ownerId}} />

				{{#if @isDeleteBtnShown}}
					<a role="button" {{on "click" this.deleteRow}}>
						<CommonUiIconComponent @name="Trash" @size="sm" />
					</a>
				{{/if}}
			</div>
		</div>
	</template>
}
